import React, { useEffect, useState } from "react";
import "../styles/navbar.css";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/auth";
import { useCart } from "../context/cart";
import { GoHeart } from "react-icons/go";
import toast from "react-hot-toast";
import { MdMenuOpen } from "react-icons/md";

const Navbar = () => {
  const [auth, setAuth] = useAuth();
  const [cart] = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Throttle scroll handler for better performance
  const changeNavbar = () => {
    if (window.scrollY >= 90) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  };

  useEffect(() => {
    const throttledScroll = throttle(changeNavbar, 100);
    window.addEventListener("scroll", throttledScroll);
    return () => window.removeEventListener("scroll", throttledScroll);
  }, []);

  const handleLogout = () => {
    setAuth({
      ...auth,
      user: null,
      token: "",
    });
    localStorage.removeItem("auth");
    toast.success("Logged Out Successfully");
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Basic throttle function for scroll events
  function throttle(func, limit) {
    let lastFunc;
    let lastRan;
    return function () {
      const context = this;
      const args = arguments;
      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(function () {
          if (Date.now() - lastRan >= limit) {
            func.apply(context, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  }

  return (
    <header
      className={`navbar-container ${isScrolled ? "scrolled" : ""}`}
      role="banner"
      itemScope
      itemType="https://schema.org/WebSite"
    >
      <div className="navbar-content">
        {/* Logo with schema markup */}
        <div
          className="navbar-brand"
          itemProp="publisher"
          itemScope
          itemType="https://schema.org/Organization"
        >
          <Link
            to="/"
            className="logo-link"
            itemProp="url"
            aria-label="Classic Motors KE - Home"
          >
            <h1 className="logo-text" itemProp="name">
              Classic Motors KE
            </h1>
            <meta itemProp="logo" content="%PUBLIC_URL%/logo.jpg" />
            <meta
              itemProp="description"
              content="Kenya's trusted car dealer specializing in new, foreign used, and locally used cars with 100% delivery record"
            />
            <meta itemProp="telephone" content="+254707308556" />
          </Link>
        </div>

        {/* Mobile menu button with better accessibility */}
        <button
          className="mobile-menu-button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-expanded={isMenuOpen}
          aria-controls="primary-navigation"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          <MdMenuOpen size={28} />
          <span className="visually-hidden">Menu</span>
        </button>

        {/* Main navigation with semantic markup */}
        <nav
          id="primary-navigation"
          className={`navbar-links ${isMenuOpen ? "open" : ""}`}
          role="navigation"
          aria-label="Primary navigation"
        >
          <div className="main-links">
            <NavLink to="/" onClick={closeMenu}>
              Home
            </NavLink>
            <NavLink to="/about" onClick={closeMenu}>
              About Us
            </NavLink>
            <NavLink to="/cars" onClick={closeMenu}>
              Our Cars
            </NavLink>
            <NavLink to="/brands" onClick={closeMenu}>
              Brands
            </NavLink>
            <CartLink cartCount={cart?.length} onClick={closeMenu} />
          </div>

          <div className="auth-links">
            {!auth.user ? (
              <>
                <AuthLink to="/login" variant="outline">
                  Login
                </AuthLink>
                <AuthLink to="/register" variant="solid">
                  Register
                </AuthLink>
              </>
            ) : (
              <>
                <AuthLink
                  to={`/dashboard/${auth?.user?.role === 1 ? "admin" : "user"}`}
                  variant="solid"
                >
                  Dashboard
                </AuthLink>
                <AuthLink
                  to="/login"
                  onClick={handleLogout}
                  variant="destructive"
                >
                  Logout
                </AuthLink>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

// Enhanced NavLink component with better SEO attributes
const NavLink = ({ to, children, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`nav-link ${isActive ? "active" : ""}`}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      itemProp="url"
    >
      <span itemProp="name">{children}</span>
    </Link>
  );
};

// Enhanced AuthLink component
const AuthLink = ({ to, children, variant = "solid", onClick, ...props }) => {
  return (
    <Link
      to={to}
      className={`auth-link ${variant}`}
      onClick={onClick}
      aria-label={children}
      {...props}
    >
      {children}
    </Link>
  );
};

// Enhanced CartLink component
const CartLink = ({ cartCount, onClick }) => {
  return (
    <Link
      to="/cart"
      className="cart-link"
      onClick={onClick}
      aria-label={`Wishlist (${cartCount} items)`}
    >
      <GoHeart size={20} aria-hidden="true" />
      {cartCount > 0 && (
        <span className="cart-count" aria-hidden="true">
          {cartCount}
        </span>
      )}
      <span className="visually-hidden">Wishlist items: {cartCount}</span>
    </Link>
  );
};

export default Navbar;
